export interface Waypoint {
  id: string;
  locationKey: string;
  sequence: number;
  tasks: Task[];
  trip?: Trip;
}

export interface Address {
  id: string;
  line1: string;
  line2?: string;
  city: string;
  province: string;
  postalCode?: string;
  country: string;
  location: {
    lat: number;
    lng: number;
  };
}

export interface Task {
  id: string;
  taskNumber: number;
  address: {
    line1: string;
    line2: string;
    city: string;
    province: string;
    postalCode: string;
    country: string;
    location: {
      lat: number;
      lng: number;
    };
  };
  type:
    | "PICKUP"
    | "DELIVERY"
    | "DEPOT_RECEIVE"
    | "DEPOT_SORT"
    | "DEPOT_DISPATCH"
    | "RETURN";
  status: "PENDING" | "IN_PROGRESS" | "COMPLETED" | "FAILED";
  description?: string;
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
  parentTask?: Task;
  childTasks: Task[];
  order: Order;
  trip?: Trip;
  tripId?: string;
  operator?: Operator;
}

export interface Operator {
  id: string;
  status: "ON_DUTY" | "OFF_DUTY" | "DELETED";
  user: User;
  lastLocation: {
    lat: number;
    lng: number;
    accuracy: number;
    altitude: number;
    heading: number;
    speed: number;
    timestamp: Date;
  };
}

export enum DepotType {
  HUB = "HUB",
  WAREHOUSE = "WAREHOUSE",
  MICRO_HUB = "MICRO_HUB",
}

export interface Depot {
  id: string;
  name: string;
  depotNumber: string;
  type: DepotType;
  status: "ACTIVE" | "INACTIVE";
  address: {
    location: {
      lat: number;
      lng: number;
    };
  };
}

export interface Trip {
  id: string;
  tripNumber: number;
  name: string;
  day: Date;
  color?: string;
  status: "PENDING" | "IN_PROGRESS" | "COMPLETED" | "FAILED";
  operator?: Operator;
  vehicle?: Vehicle;
  plannedRoute?: {
    routes: {
      polyline: {
        encodedPolyline: string;
      };
    }[];
  };
}

export interface Vehicle {
  id: string;
  plateNumber: string;
}

export interface ServiceArea {
  id: string;
  name: string;
  code: string;
  depotId: string;
  metadata: Record<string, any>;
}

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  picture?: string;
}

export interface Customer {
  id: string;
  user: User;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  sentOrders: Order[];
  receivedOrders: Order[];
  addresses: CustomerAddress[];
  customerNumber: number;
  tierId?: string;
  businessAccount?: BusinessAccount;
  type: "INDIVIDUAL" | "BUSINESS";
  billing?: Billing;
}

export interface BusinessAccount {
  id: string;
  companyName: string;
  taxNumber?: string;
  registrationNo?: string;
  addressId?: string;
  address?: Address;
}

export interface CustomerAddress {
  id: string;
  address: Address;
  isPrimary: boolean;
  label?: string;
  pickupNotes?: string;
  deliveryNotes?: string;
  name?: string;
  phone?: string;
  createdAt: string;
  updatedAt: string;
  customerId: string;
}

export interface Parcel {
  id: string;
  trackingNumber: string;
  status: string;
  dimensions: {
    length: number;
    width: number;
    height: number;
  };
  weight: number;
  isFragile: boolean;
  requiresCooling: boolean;
  requiresWarm: boolean;
  value: number;
  description: string;
  metadata: Record<string, any>;
}

export enum OrderStatus {
  REGISTERED = "REGISTERED",
  PICKUP_PENDING = "PICKUP_PENDING",
  OUT_FOR_PICKUP = "OUT_FOR_PICKUP",
  PICKUP_ASSIGNED = "PICKUP_ASSIGNED",
  PICKED_UP = "PICKED_UP",
  DEPOT_RECEIVE_PENDING = "DEPOT_RECEIVE_PENDING",
  DEPOT_RECEIVE_IN_PROGRESS = "DEPOT_RECEIVE_IN_PROGRESS",
  AT_DEPOT = "AT_DEPOT",
  DEPOT_SORT_PENDING = "DEPOT_SORT_PENDING",
  DEPOT_SORT_IN_PROGRESS = "DEPOT_SORT_IN_PROGRESS",
  SORTED = "SORTED",
  DEPOT_DISPATCH_PENDING = "DEPOT_DISPATCH_PENDING",
  DEPOT_DISPATCH_IN_PROGRESS = "DEPOT_DISPATCH_IN_PROGRESS",
  IN_TRANSIT = "IN_TRANSIT",
  DELIVERY_PENDING = "DELIVERY_PENDING",
  OUT_FOR_DELIVERY = "OUT_FOR_DELIVERY",
  DELIVERED = "DELIVERED",
  RETURN_PENDING = "RETURN_PENDING",
  RETURN_IN_PROGRESS = "RETURN_IN_PROGRESS",
  RETURNED = "RETURNED",
  PICKUP_FAILED = "PICKUP_FAILED",
  DEPOT_RECEIVE_FAILED = "DEPOT_RECEIVE_FAILED",
  DEPOT_SORT_FAILED = "DEPOT_SORT_FAILED",
  DEPOT_DISPATCH_FAILED = "DEPOT_DISPATCH_FAILED",
  DELIVERY_FAILED = "DELIVERY_FAILED",
  RETURN_FAILED = "RETURN_FAILED",
  PICKUP_CANCELLED = "PICKUP_CANCELLED",
  DEPOT_RECEIVE_CANCELLED = "DEPOT_RECEIVE_CANCELLED",
  DEPOT_SORT_CANCELLED = "DEPOT_SORT_CANCELLED",
  DEPOT_DISPATCH_CANCELLED = "DEPOT_DISPATCH_CANCELLED",
  DELIVERY_CANCELLED = "DELIVERY_CANCELLED",
  RETURN_CANCELLED = "RETURN_CANCELLED",
  CANCELLED = "CANCELLED",
  PARTIALLY_PICKED_UP = "PARTIALLY_PICKED_UP",
  PARTIALLY_DELIVERED = "PARTIALLY_DELIVERED",
}

export interface Order {
  id: string;
  orderNumber: string;
  status: string;
  type: string;
  senderName: string;
  senderEmail: string;
  senderPhone: string;
  senderCustomerId: string;
  pickupDepotId: string;
  deliveryDepotId: string;
  recipientName: string;
  recipientEmail: string;
  recipientPhone: string;
  pickupAddress: Address;
  deliveryAddress: Address;
  parcels: Parcel[];
  totalFees: number;
  createdAt: string;
  appliedFees?: { amount: number }[];
  pickupNotes?: string;
  deliveryNotes?: string;
}

export interface PricingOption {
  speedType: string;
  speedDescription: string;
  estimatedDeliveryTime: { minHours: number; maxHours: number };
  price: number;
  breakdown: {
    baseFee: { amount: number };
    speedFee: { amount: number };
    dimensionalWeightFee: { amount: number };
    specialHandlingFees: { amount: number };
    insuranceFee: { amount: number };
  };
}

export interface PricingOptions {
  pickupServiceArea: ServiceArea;
  deliveryServiceArea: ServiceArea;
  totalWeight: number;
  totalDimensionalWeight: number;
  chargeableWeight: number;
  shippingOptions: PricingOption[];
  reason?: string;
}

export interface Invoice {
  id: string;
  customerId: string;
  createdAt: string;
  updatedAt: string;
  stripeInvoiceData: {
    id: string;
    number: string;
    status: string;
    hostedUrl: string | null;
    pdfUrl: string | null;
    amount: number;
    dueDate: number | null; // unix timestamp
  };
  invoiceOrders: {
    order: {
      orderNumber: string;
      appliedFees: {
        amount: number;
      }[];
    };
  }[];
  orderDateRange: {
    start: string;
    end: string;
  } | null;
  totalOrders: number;
}

export enum PaymentStyle {
  MANUAL = "MANUAL",
  PAY_PER_ORDER = "PAY_PER_ORDER",
  MONTHLY_FIRST = "MONTHLY_FIRST",
  WEEKLY_SUNDAY = "WEEKLY_SUNDAY",
}

export interface PaymentMethod {
  id: string;
  type: string;
  last4: string;
  brand?: string;
  isDefault: boolean;
  expiryMonth?: number;
  expiryYear?: number;
}

export interface Billing {
  id: string;
  address: Address;
  stripeCustomerId: string;
  paymentStyle: PaymentStyle;
  paymentMethods: PaymentMethod[];
  customer: Customer;
  createdAt: string;
  updatedAt: string;
}
